import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowDownTray } from "react-icons/hi2";
import { useTranslation } from "react-i18next";

import headerBanner from "@/assets/dashboard/headerpanel.png";
import { useSession } from "@/core/auth/hooks/useSession";
import {
  useMisProductos,
  useMisProductosDetalle,
} from "@/features/dashboard/hooks/useMisProductos";
import {
  DocumentDownloadModal,
  type DocumentDownloadOption,
} from "@/features/dashboard/components/DocumentDownloadModal";
import { DocumentDownloadErrorModal } from "@/features/dashboard/components/DocumentDownloadErrorModal";
import { misProductosService } from "@/features/dashboard/services/misProductos.service";
import { contactoService } from "@/features/contacto/services/contacto.service";
import { BarChart } from "@/shared/components/BarChart";
import { formatPersonName, formatRate, round2 } from "@/shared/utils/formatters";

function formatMoney(
  value: number | null | undefined,
  locale?: string,
): string {
  if (value == null) return "-";
  return new Intl.NumberFormat(locale === "en" ? "en-US" : "es-EC", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(iso: string | null | undefined, locale?: string): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString(locale === "en" ? "en-US" : "es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function DashboardPage() {
  const { t, i18n } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { profile } = useSession();
  const { productos, loading, error } = useMisProductos(i18n.language);
  const [activeIndex, setActiveIndex] = useState(0);
  const [descargando, setDescargando] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadOption, setDownloadOption] =
    useState<DocumentDownloadOption | null>(null);
  const [downloadSuccessFile, setDownloadSuccessFile] = useState<string | null>(
    null,
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [contactWhatsapp, setContactWhatsapp] = useState("");

  useEffect(() => {
    void contactoService.obtener()
      .then((contacto) => {
        setContactWhatsapp(contacto?.telefono?.replace(/\D/g, "") ?? "");
      })
      .catch((error) => {
        console.error("[Contacto] No se pudo cargar el teléfono de WhatsApp", error);
      });
  }, []);

  const monthLabels = t("chart.months", { returnObjects: true }) as string[];

  const productoActivo = productos[activeIndex] ?? null;
  const { detalle, loading: loadingDetalle } = useMisProductosDetalle(
    productoActivo?.id ?? null,
    i18n.language,
  );

  const inversionBase = productoActivo?.capital ?? 0;

  const cuotas = detalle?.cuotas ?? [];

  const chartData = cuotas
    .filter((c) => c.valorAcumuladoSinBono != null && c.valorAcumulado != null)
    .map((c) => {
    let label: string;
    if (c.fechaInicial) {
      const d = new Date(c.fechaInicial);
      label = `${monthLabels[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`;
    } else {
      label = `M${c.periodo ?? ""}`;
    }

    const bonus = c.bonoAnual ?? c.bonoAPagarAnual ?? 0;
      return { label, value: c.valorAcumuladoSinBono!, bonus: bonus > 0 ? bonus : undefined };
    });

  const chartMax =
    chartData.length > 0
      ? Math.max(...chartData.map((d) => d.value + (d.bonus ?? 0)))
      : 0;

  const yLabels =
    chartMax > 0
      ? [
          `$${((inversionBase + chartMax) / 1000).toFixed(1)}k`,
          `$${((inversionBase + chartMax / 2) / 1000).toFixed(1)}k`,
          `$${(inversionBase / 1000).toFixed(1)}k`,
        ]
      : ["$0.0k", "$0.0k", "$0.0k"];

  const rentaPeriodo = detalle?.totalRentaPeriodo ?? null;

  const openDownloadModal = () => {
    setDownloadOption(null);
    setDownloadSuccessFile(null);
    setDownloadError(null);
    setShowDownloadErrorModal(false);
    setShowDownloadModal(true);
  };

  const closeDownloadModal = () => {
    if (descargando) return;
    setShowDownloadModal(false);
    setDownloadOption(null);
    setDownloadSuccessFile(null);
    setDownloadError(null);
  };

  const handleDownloadDocument = async () => {
    if (!productoActivo || !downloadOption || descargando) return;
    setDownloadError(null);
    setDescargando(true);
    try {
      const isEnglish = i18n.language?.split('-')[0] === "en";
      const projectionFileName = isEnglish
        ? "investment-projection.pdf"
        : "proyeccion-de-la-inversion.pdf";
      const contractFileName = isEnglish
        ? "contracts-and-signed-legal-annexes.pdf"
        : "contratos-y-anexos-legales-formados.pdf";

      if (downloadOption === "projection") {
        const documentos = await misProductosService.obtenerDocumentos(
          productoActivo.id,
          i18n.language,
        );
        const projection = documentos.documents.find(
          (documento) => documento.documentCode === "ANEXOSYNERGYPDF" &&
            documento.documentGroup === "PROJECTION",
        );

        if (!projection) {
          throw new Error("No existe el documento de proyección configurado.");
        }

        await misProductosService.descargarPdf(
          productoActivo.id,
          i18n.language,
          projectionFileName,
        );
        setDownloadSuccessFile(projectionFileName);
      } else {
        console.info("[Documentos] Iniciando consolidación contractual", productoActivo.id);
        const job = await misProductosService.crearJobContrato(
          productoActivo.id,
          i18n.language,
        );
        let estado = await misProductosService.consultarJobContrato(job.jobId);

        for (let attempt = 0; attempt < 60 &&
          (estado.status === "PENDING" || estado.status === "PROCESSING"); attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 1000));
          estado = await misProductosService.consultarJobContrato(job.jobId);
        }

        if (estado.status !== "COMPLETED") {
          throw new Error(estado.errorMessage ?? "No se pudo consolidar el contrato.");
        }

        const filename = await misProductosService.descargarContrato(
          job.jobId,
          contractFileName,
        );
        setDownloadSuccessFile(filename);
      }
    } catch (error) {
      // Mantener el mensaje amigable para el usuario, pero conservar el detalle
      // en consola para identificar si falló la creación, el polling o la descarga.
      console.error("[Dashboard] Error en el flujo de descarga documental", error);
      const detail = error instanceof Error ? error.message.trim() : "";
      setDownloadError(detail || t("downloadError"));
      setShowDownloadModal(false);
      setShowDownloadErrorModal(true);
    } finally {
      setDescargando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="h-28 rounded-md bg-[linear-gradient(90deg,var(--color-border-soft)_25%,#e8eff6_50%,var(--color-border-soft)_75%)] bg-size-[200%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite]" />
        <div className="h-80 rounded-md bg-[linear-gradient(90deg,var(--color-border-soft)_25%,#e8eff6_50%,var(--color-border-soft)_75%)] bg-size-[200%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-8 py-16 text-center">
        <p className="m-0 text-body-3 text-text-muted">
          {error}
        </p>
      </div>
    );
  }

  return (
    <>
      <img
        src={headerBanner}
        alt={t("bannerAlt")}
        className="mx-6 mt-4 block aspect-[1031/113] w-[calc(100%-3rem)] rounded-sm object-contain max-md:mx-4 max-md:w-[calc(100%-2rem)]"
      />

      <div className="px-6 py-4 max-md:px-4">
        <div className="mb-3">
          <h1 className="m-0 text-body-1 font-semibold text-text-primary">
            {t("greeting", {
              name: formatPersonName(profile?.firstName),
            })}
          </h1>
          <p className=" m-0 text-body-3 font-normal text-[#05141F]">
            {t("greetingSubtitle")}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {productoActivo && (
            <div className="grid items-stretch gap-x-5 gap-y-4 max-[1023px]:grid-cols-1 min-[1024px]:grid-cols-[minmax(0,1fr)_19rem]">
              {productos.length > 0 && (
                <div
                  className="col-span-full row-start-1 flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-[2px] [&::-webkit-scrollbar-thumb]:bg-white/25 [&::-webkit-scrollbar-track]:bg-transparent"
                  role="tablist"
                >
                  {productos.map((p, i) => (
                    <button
                      key={p.id}
                      role="tab"
                      aria-selected={i === activeIndex}
                      className={`flex flex-col items-start justify-center gap-2.5 w-[270px] h-[145px] flex-[0_0_270px] overflow-hidden snap-start cursor-pointer min-w-[14rem] rounded-sm border-[1.5px] border-transparent px-11 py-3 text-left transition-[border-color,background] duration-150 ${
                        i === activeIndex
                          ? "bg-[linear-gradient(180deg,#1c3042_0%,#00558e_48.56%,#007acc_100%)] text-white"
                          : "bg-[linear-gradient(180deg,#299ad9_0%,#6ab9e5_71.63%)] text-white hover:bg-[#004f84]"
                      }`}
                      onClick={() => setActiveIndex(i)}
                      type="button"
                    >
                      <span className="block text-[14px] font-light tracking-[0.03em] text-white">
                        {p.tipoProducto ?? `Producto ${i + 1}`}
                      </span>
                      {p.capital != null && (
                        <span className="block text-[26px] font-light leading-[1.2] text-white">
                          {formatMoney(p.capital, i18n.language)}
                        </span>
                      )}
                      {p.numeroContrato && (
                        <span className="block font-base text-[15px] font-light tracking-[0.02em] text-white">
                          {p.numeroContrato}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div
                className={`flex flex-col max-[1023px]:order-2 min-[1024px]:col-start-1 ${productos.length > 0 ? "min-[1024px]:row-start-2" : "min-[1024px]:row-start-1"} min-w-0 rounded-md bg-[linear-gradient(180deg,#ffffff_0%,#eef7fc_100%)] p-4`}
              >
                {loadingDetalle ? (
                  <div className="h-36 rounded-sm bg-[linear-gradient(90deg,var(--color-border-soft)_25%,#e8eff6_50%,var(--color-border-soft)_75%)] bg-size-[200%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite]" />
                ) : chartData.length > 0 ? (
                  <>
                    <p className="mb-4 text-center text-[1.0625rem] font-semibold text-[#104063]">
                      {t("chart.title")}
                    </p>
                    <div className="flex min-w-0 gap-2 items-start overflow-x-auto overflow-y-visible pb-6 [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#acd8f0] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#0066aa]">
                      <div className="shrink-0 pb-5">
                        <div className="flex h-48 flex-col justify-between items-end whitespace-nowrap text-[0.7rem] font-medium text-text-muted">
                          {yLabels.map((label, i) => (
                            <span key={i}>{label}</span>
                          ))}
                        </div>
                      </div>
                      <div className="min-w-max flex-1 overflow-visible pb-6">
                        <BarChart
                          data={chartData}
                          min={inversionBase}
                          ariaLabel={t("chart.ariaLabel")}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-[0.875rem] text-text-muted">
                    {t("chart.noData")}
                  </p>
                )}
              </div>

              <div
                className={`max-[1023px]:order-1 min-[1024px]:col-start-2 ${productos.length > 0 ? "min-[1024px]:row-start-2" : "min-[1024px]:row-start-1"} overflow-hidden rounded border-0 outline-none shadow-card bg-[linear-gradient(180deg,#1c3042_0%,#00558e_48.56%,#007acc_100%)]`}
              >
                <div className="relative flex items-start justify-between bg-transparent px-4 py-3 pr-[calc(2.75rem+var(--space-3))]">
                  <div className="py-3">
                    <h2 className="m-0 text-body-2 font-bold text-white">
                      {productoActivo.tipoProducto ?? "Mi Producto"}
                    </h2>
                  </div>
                  <button
                    type="button"
                    className={`absolute top-0 right-0 z-10 flex h-11 w-11 items-center justify-center rounded-bl rounded-tr-[0.25rem] border-0 bg-white text-[1.125rem] text-[#003d66] transition-colors duration-150 isolation-isolate ${
                      descargando
                        ? "cursor-not-allowed opacity-70"
                        : "cursor-pointer hover:bg-[#f0f7ff]"
                    }`}
                    aria-label={t("download")}
                    disabled={descargando}
                    onClick={openDownloadModal}
                  >
                    {descargando ? (
                      <svg
                        className="w-5 h-5 animate-[spin_0.9s_linear_infinite]"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray="31.4 31.4"
                        />
                      </svg>
                    ) : (
                      <HiArrowDownTray aria-hidden />
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-4 mx-4 mb-2 rounded border-0 outline-none bg-[#003D66] px-4 py-2">
                  <span className="block shrink-0 text-[0.75rem] font-medium leading-[1.3] whitespace-pre-line text-white/70">
                    {t("capitalRendimiento")}
                  </span>
                  <span className="mt-1 block min-w-0 justify-self-end text-right whitespace-nowrap text-[1rem] font-bold text-white">
                    {formatMoney(
                      (productoActivo.capital ?? 0) +
                        (productoActivo.rendimiento ?? 0),
                      i18n.language,
                    )}
                  </span>
                </div>

                <div className="bg-transparent px-4 py-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("contract")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {productoActivo.numeroContrato ?? "-"}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("fields.capital")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {formatMoney(productoActivo.capital, i18n.language)}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("fields.plazo")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {productoActivo.plazo != null
                          ? t("fields.plazoValue", {
                              months: productoActivo.plazo,
                            })
                          : "-"}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("fields.tasaInteres")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {productoActivo.tasaInteres != null
                          ? `${formatRate(round2(productoActivo.tasaInteres))}%`
                          : "-"}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("fields.rentaPeriodo")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {formatMoney(rentaPeriodo, i18n.language)}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("fields.bonoAnual")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {formatMoney(
                          detalle?.bonoAnual ?? detalle?.totalSumaDeBonos ?? null,
                          i18n.language,
                        )}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("fields.fechaInicio")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {formatDate(productoActivo.fechaInicio, i18n.language)}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1 leading-[1.6]">
                      <span className="shrink-0 text-[0.75rem] font-normal text-white after:content-[':']">
                        {t("fields.fechaVencimiento")}
                      </span>
                      <span className="text-[0.75rem] font-medium text-white">
                        {formatDate(
                          productoActivo.fechaVencimiento,
                          i18n.language,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showDownloadModal && (
        <DocumentDownloadModal
          selectedOption={downloadOption}
          downloading={descargando}
          successFileName={downloadSuccessFile}
          error={downloadError}
          title={t("downloadOptions.title")}
          subtitle={t("downloadOptions.subtitle")}
          projectionTitle={t("downloadOptions.projection.title")}
          projectionDescription={t("downloadOptions.projection.description")}
          contractTitle={t("downloadOptions.contract.title")}
          contractDescription={t("downloadOptions.contract.description")}
          contractPending={t("downloadOptions.contract.pending")}
          downloadLabel={t("download")}
          cancelLabel={t("downloadOptions.cancel")}
          successTitle={t("downloadOptions.successTitle")}
          successDescription={
            downloadOption === "projection"
              ? t("downloadOptions.successDescriptionProjection")
              : t("downloadOptions.successDescription")
          }
          backLabel={t("downloadOptions.back")}
          folderLabel={t("downloadOptions.folder")}
          folderNotice={t("downloadOptions.folderNotice")}
          fileGeneratedLabel={t("downloadOptions.fileGeneratedLabel")}
          onSelect={(option) => {
            setDownloadOption(option);
            setDownloadError(null);
          }}
          onDownload={() => void handleDownloadDocument()}
          onClose={closeDownloadModal}
        />
      )}
      {showDownloadErrorModal && (
        <DocumentDownloadErrorModal
          title={t("downloadErrorModal.title")}
          description={t("downloadErrorModal.description")}
          understoodLabel={t("downloadErrorModal.understood")}
          contactLabel={t("downloadErrorModal.contact")}
          onUnderstood={() => {
            setShowDownloadErrorModal(false);
            setDownloadError(null);
          }}
          onContact={() => {
            setShowDownloadErrorModal(false);
            setDownloadError(null);
            if (contactWhatsapp) {
              window.open(
                `https://wa.me/${contactWhatsapp}`,
                "_blank",
                "noopener,noreferrer",
              );
            } else {
              navigate("/contacto");
            }
          }}
        />
      )}
    </>
  );
}
